/**
 * The rules-ingestion surface, worn in Ant Design (the proof screen of
 * the 2026-08-06 framework ruling): upload a code document (NEC, IPC,
 * IMC...) and watch its parsed sections queue -- classification with
 * the NULL set shown honestly, the check-back anchor (source file +
 * line span), and the one-time verification against the authoritative
 * source. UNVERIFIED wears a loud tag: like UNPRICED in takeoff, the
 * honesty is the feature.
 *
 * Until THE RULES REBUILD lands its endpoints, the backend reports
 * pending and this page says exactly that -- the upload contract is
 * live in the form either way.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { InboxOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Input, Select, Space, Table, Typography, Upload } from 'antd';
import { AbsenceBadge, ApprovalMark } from '../../../strata';
import { type RuleSourceSection, useRuleSections, useUploadRuleSource, useVerifySection } from '../queries';

const BOOKS = ['NEC', 'IPC', 'IMC', 'IBC'];

export function RuleSourcesPanel() {
  const [book, setBook] = useState(BOOKS[0]);
  const [edition, setEdition] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const sections = useRuleSections();
  const upload = useUploadRuleSource();
  const verify = useVerifySection();

  const backendPending =
    sections.data?.backendPending === true || upload.data?.backendPending === true;
  const rows = sections.data?.backendPending === false ? sections.data.sections : [];
  const canSubmit = file !== null && edition.trim() !== '' && !upload.isPending;

  const columns = [
    { title: 'Section', dataIndex: 'section', width: 110 },
    { title: 'Title', dataIndex: 'title' },
    {
      title: 'Book',
      key: 'book',
      width: 90,
      render: (record: RuleSourceSection) => `${record.code} ${record.edition}`,
    },
    {
      title: 'Classification',
      key: 'classification',
      width: 130,
      render: (record: RuleSourceSection) =>
        record.classification ?? (
          <AbsenceBadge label="NULL" reason="no measurement of ours speaks this section yet" />
        ),
    },
    {
      title: 'Source anchor',
      key: 'anchor',
      width: 200,
      render: (record: RuleSourceSection) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {record.source_file} · lines {record.line_start}–{record.line_end}
        </Typography.Text>
      ),
    },
    {
      title: 'Check-back',
      key: 'checkback',
      width: 250,
      render: (record: RuleSourceSection) =>
        record.verified_by === null ? (
          <Space>
            <AbsenceBadge
              label="UNVERIFIED"
              reason="not yet checked back against the authoritative source"
            />
            <Button
              type="link"
              size="small"
              disabled={verify.isPending}
              onClick={() => verify.mutate(record.id)}
            >
              Mark verified against source
            </Button>
          </Space>
        ) : (
          <ApprovalMark decision="verified" who={record.verified_by} />
        ),
    },
    {
      title: 'Ratify',
      key: 'ratify',
      width: 110,
      // The link shows once a measurement speaks the section; the
      // SERVER still enforces the verification precondition (a
      // scrape-grade section 409s until checked back).
      render: (record: RuleSourceSection) =>
        record.classification !== null ? (
          <Link to="/rules/sections/$sectionId" params={{ sectionId: record.id }}>
            Ratify →
          </Link>
        ) : null,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Upload.Dragger
        accept=".txt,.html,.htm,.pdf"
        maxCount={1}
        fileList={file ? [{ uid: '1', name: file.name }] : []}
        beforeUpload={(picked) => {
          setFile(picked);
          return false;
        }}
        onRemove={() => setFile(null)}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag a code document to this area</p>
        <p className="ant-upload-hint">
          PDF, HTML, or text. Sections are parsed on arrival; the classification ladder
          runs automatically.
        </p>
      </Upload.Dragger>

      <Space wrap>
        <Select
          value={book}
          onChange={setBook}
          options={BOOKS.map((name) => ({ value: name, label: name }))}
          style={{ width: 110 }}
        />
        <Input
          placeholder="Edition (e.g. 2023)"
          value={edition}
          onChange={(event) => setEdition(event.target.value)}
          style={{ width: 170 }}
        />
        <Button
          type="primary"
          loading={upload.isPending}
          disabled={!canSubmit}
          onClick={() => {
            if (file && edition.trim()) {
              upload.mutate({ file, code: book, edition: edition.trim() });
            }
          }}
        >
          Upload & process
        </Button>
      </Space>

      {backendPending && (
        <Alert
          type="warning"
          showIcon
          message="The processing backend lands with the rules rebuild -- the upload contract is ready, and this page goes live the moment those endpoints exist."
        />
      )}

      {rows.length > 0 && (
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      )}
      {!backendPending && sections.isSuccess && rows.length === 0 && (
        <Empty description="No sections yet -- upload a code document above." />
      )}
    </Space>
  );
}
